// Photos: shrinking on the phone, reading the capture time and place (EXIF), and showing stored photos.
import { dbGet } from "./db.ts";

/** A JPEG at most `max` px on the longer side. */
export async function shrink(file: Blob, max: number, quality: number): Promise<Blob> {
  let src: ImageBitmap | HTMLImageElement;
  try { src = await createImageBitmap(file, { imageOrientation: "from-image" }); }
  catch {
    src = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file); });
  }
  const k = Math.min(1, max / Math.max(src.width, src.height));
  const c = document.createElement("canvas");
  c.width = Math.round(src.width * k); c.height = Math.round(src.height * k);
  c.getContext("2d")!.drawImage(src, 0, 0, c.width, c.height);
  return new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("photo could not be encoded"))), "image/jpeg", quality));
}

export const blobToDataUrl = (b: Blob): Promise<string> => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(b);
});
export const dataUrlToBlob = async (d: string): Promise<Blob> => (await fetch(d)).blob();

export interface Exif { time?: number; loc?: { lat: number; lon: number } }

/** The photo's capture time and GPS position from its EXIF data, when the phone kept them. */
export async function readExif(file: Blob): Promise<Exif | null> {
  try {
    const dv = new DataView(await file.slice(0, 256 * 1024).arrayBuffer());
    if (dv.getUint16(0) !== 0xFFD8) return null; // not a JPEG
    let o = 2;
    while (o + 4 < dv.byteLength) {
      const marker = dv.getUint16(o), len = dv.getUint16(o + 2);
      if (marker === 0xFFE1 && dv.getUint32(o + 4) === 0x45786966) return parseTiff(dv, o + 10); // "Exif"
      if ((marker & 0xFF00) !== 0xFF00) break;
      o += 2 + len;
    }
  } catch { /* unreadable: no EXIF */ }
  return null;
}
function parseTiff(dv: DataView, base: number): Exif {
  const le = dv.getUint16(base) === 0x4949;
  const u16 = (o: number) => dv.getUint16(base + o, le), u32 = (o: number) => dv.getUint32(base + o, le);
  // tag number → offset of its directory entry
  const ifd = (o: number) => { const n = u16(o), m: Record<number, number> = {}; for (let i = 0; i < n; i++) { const e = o + 2 + i * 12; m[u16(e)] = e; } return m; };
  const ascii = (e: number) => { const n = u32(e + 4), off = n > 4 ? u32(e + 8) : e + 8; let s = ""; for (let i = 0; i < n - 1; i++) s += String.fromCharCode(dv.getUint8(base + off + i)); return s; };
  const rationals = (e: number) => { const off = u32(e + 8), r: number[] = []; for (let i = 0; i < 3; i++) r.push(u32(off + i * 8) / (u32(off + i * 8 + 4) || 1)); return r; };
  const out: Exif = {}, ifd0 = ifd(u32(4));
  if (ifd0[0x8769]) {
    const ex = ifd(u32(ifd0[0x8769] + 8));
    const dt = ex[0x9003] || ex[0x9004]; // DateTimeOriginal, DateTimeDigitized
    if (dt) {
      const m = ascii(dt).match(/(\d{4}):(\d\d):(\d\d) (\d\d):(\d\d):(\d\d)/);
      if (m) out.time = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime();
    }
  }
  if (ifd0[0x8825]) {
    const g = ifd(u32(ifd0[0x8825] + 8));
    if (g[2] && g[4]) {
      const dms = (a: number[]) => a[0] + a[1] / 60 + a[2] / 3600;
      let lat = dms(rationals(g[2])), lon = dms(rationals(g[4]));
      if (g[1] && ascii(g[1]) === "S") lat = -lat;
      if (g[3] && ascii(g[3]) === "W") lon = -lon;
      if (Number.isFinite(lat) && Number.isFinite(lon) && (lat || lon)) out.loc = { lat, lon };
    }
  }
  return out;
}

const urlCache = new Map<string, string>();
/** An object URL for a stored photo (cached), or null when it's gone. */
export async function photoUrl(id: string, size: "thumb" | "full" = "thumb"): Promise<string | null> {
  const key = id + ":" + size;
  const hit = urlCache.get(key);
  if (hit) return hit;
  const p = await dbGet("photos", id).catch(() => undefined);
  if (!p?.[size]) return null;
  const u = URL.createObjectURL(p[size]);
  urlCache.set(key, u);
  return u;
}
export function dropPhotoUrls(id: string): void {
  for (const size of ["thumb", "full"]) {
    const u = urlCache.get(id + ":" + size);
    if (u) { URL.revokeObjectURL(u); urlCache.delete(id + ":" + size); }
  }
}
